import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  getAllTemplatePlugins,
  deleteTemplatePlugin,
  addTemplatePlugin,
  updateTemplatePlugin,
} from "redux/reducers/templateLibrary/plugins";
import { axiosInstance, printError, useUtilities } from "helpers";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export async function getPlugins() {
  return axiosInstance
    .get("allPlugins", {
      params: {
        api_key: localStorage.getItem("apikey"),
      },
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useGetAllPlugins() {
  return useQuery(["allPlugins"], getPlugins);
}

export function usePlugins() {
  const {
    dispatch,
    plugins: { plugins, loaded },
  } = useUtilities();
  const { data: pluginData, isLoading, error } = useGetAllPlugins();

  useEffect(() => {
    if (isLoading || error || loaded || !pluginData) return;
    if (pluginData.status_code !== 200) return;
    dispatch(getAllTemplatePlugins(pluginData.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pluginData, isLoading, error, loaded]);

  return { data: plugins, isLoading, error };
}

export async function addPlugin(data) {
  return axiosInstance
    .post("addplugin", {
      ...data,
      api_key: localStorage.getItem("apikey"),
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useAddPlugin(
  redirect = `/${ModulePrefixes.TEMPLATE_LIBRARY}/plugins`,
  showNotification = true
) {
  const { notification, dispatch, navigate } = useUtilities();

  return useMutation(addPlugin, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(addTemplatePlugin(data.data));
        if (showNotification)
          notification({
            type: "success",
            message: t("libraryTemplate.plugins.api.success.added"),
          });
        if (redirect) navigate(redirect);
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: err.message,
      });
    },
  });
}

export async function deletePlugin(plugin_id) {
  return axiosInstance
    .post("delplugin", {
      plugin_id,
      api_key: localStorage.getItem("apikey"),
    })
    .then((res) => ({ ...res.data, id: plugin_id }))
    .catch((err) => new Error(err));
}

export function useDeletePlugin(redirect = "") {
  const { notification, dispatch, navigate } = useUtilities();
  return useMutation(deletePlugin, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        notification({
          type: "success",
          message: t("libraryTemplate.plugins.api.success.deleted"),
        });
        dispatch(deleteTemplatePlugin(data.id));
        if (redirect) navigate(redirect);
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (err) =>
      notification({
        type: "error",
        message: err.message,
      }),
  });
}

async function updatePlugin(data) {
  return axiosInstance
    .post("updateplugin", {
      ...data,
      api_key: localStorage.getItem("apikey"),
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useUpdatePlugin() {
  const { notification, dispatch } = useUtilities();

  return useMutation(updatePlugin, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(updateTemplatePlugin(data.data));
        notification({
          type: "success",
          message: t("libraryTemplate.plugins.api.success.updated"),
        });
      } else {
        notification({
          type: "error",
          message: printError(data.message),
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: printError(err.message),
      });
    },
  });
}
